import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { AppLanguage, StorePagination } from 'AppTypes'
import { Store, StoreUser } from '@app/models'
import { api } from '@services'

import i18n from '../../i18next/i18n'

export const initialState = ({
  isLoading: false,
  isMainAppSidebarOpen: false,
  language: 'jp' as AppLanguage,
  isMainLayoutInitialed: false,
  isStoreRegistrationLayoutInitialed: false,
  currentStoreUser: {} as StoreUser,
  currentStore: {} as Store,
  stores: {
    data: [] as Store[],
    pageCount: 0,
    pageIndex: 0,
    pageSize: 0,
    totalRecords: 0
  } as StorePagination
})
/* eslint no-debugger: "off" */
const fetchCurrentStoreUser = async (): Promise<StoreUser> => {
  const currentStoreUserId = localStorage.getItem('storeUserId') || ''
  const currentStoreUser = await api.storeUser.fetchStoreUserById(currentStoreUserId)
  return currentStoreUser
}

const fetchCurrentStore = async (currentStoreUser: StoreUser): Promise<Store> => {
  const currentStore = await api.store.fetchStore(currentStoreUser.currentStoreId)
  return currentStore
}

const fetchUserStores = async (currentStoreUser: StoreUser): Promise<StorePagination> => {
  let stores = initialState.stores
  if ((currentStoreUser.storeIds || []).length > 1) {
    stores = await api.store.fetchStores()
  }

  return stores
}

export const initMainLayout = createAsyncThunk<{
  currentStoreUser: StoreUser,
  stores: StorePagination,
  currentStore: Store
}>(
  'app/initMainLayout',
  async () => {
    const currentStoreUser = await fetchCurrentStoreUser()
    const currentStore = await fetchCurrentStore(currentStoreUser)
    const stores = await fetchUserStores(currentStoreUser)

    return ({
      currentStoreUser,
      currentStore,
      stores
    })
  }
)

export const initStoreRegistrationLayout = createAsyncThunk<{
  currentStoreUser: StoreUser,
  stores: StorePagination
}>(
  'app/initStoreRegistrationLayout',
  async () => {
    const currentStoreUser = await fetchCurrentStoreUser()
    const stores = await fetchUserStores(currentStoreUser)

    return ({
      currentStoreUser,
      stores
    })
  }
)

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    toggleGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    toggleMainAppSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isMainAppSidebarOpen = action.payload
    },
    switchLanguage: (state, action: PayloadAction<AppLanguage>) => {
      state.language = action.payload
      i18n.changeLanguage(action.payload).then(() => {})
    }
  },
  extraReducers: builder => {
    builder.addCase(initMainLayout.pending, state => {
      state.isLoading = true
    })
    builder.addCase(initMainLayout.fulfilled, (state, action) => {
      state.isLoading = false
      state.currentStoreUser = action.payload.currentStoreUser
      state.currentStore = action.payload.currentStore
      state.stores = action.payload.stores
      state.isMainLayoutInitialed = true
    })
    builder.addCase(initMainLayout.rejected, (state, action) => {
      state.isLoading = false
      state.isMainLayoutInitialed = true
    })
    builder.addCase(initStoreRegistrationLayout.pending, state => {
      state.isLoading = true
    })
    builder.addCase(initStoreRegistrationLayout.fulfilled, (state, action) => {
      state.isLoading = false
      state.currentStoreUser = action.payload.currentStoreUser
      state.stores = action.payload.stores
      state.isStoreRegistrationLayoutInitialed = true
    })
    builder.addCase(initStoreRegistrationLayout.rejected, (state, action) => {
      state.isLoading = false
      state.isStoreRegistrationLayoutInitialed = true
    })
  }
})

export const {
  toggleGlobalLoading,
  toggleMainAppSidebarOpen,
  switchLanguage
} = appSlice.actions

export default appSlice.reducer
